import {DateTime} from 'luxon';

export const buildDefaultDateFilters = () => {
    const now = DateTime.now();
    const currentStart = now.startOf('year').toISODate();
    const currentEnd = now.toISODate();
    const priorStart = now.minus({years: 1}).startOf('year').toISODate();
    const priorEnd = now.minus({years: 1}).toISODate();

    return {
        currentPeriodStart: currentStart,
        currentPeriodEnd: currentEnd,
        priorPeriodStart: priorStart,
        priorPeriodEnd: priorEnd,
        granularity: 'M',
        businessUnitId: null,
        accountId: null,
        locationId: null,
        salesPersonId: null,
        productCategoryId: null
    };
};

export const buildV2QueryParams = (filters) => {
    const paramStrings = [];
    if (filters.currentPeriodStart) {
        paramStrings.push(`current_period_start=${filters.currentPeriodStart}`);
    }
    if (filters.currentPeriodEnd) {
        paramStrings.push(`current_period_end=${filters.currentPeriodEnd}`);
    }
    if (filters.priorPeriodStart) {
        paramStrings.push(`prior_period_start=${filters.priorPeriodStart}`);
    }
    if (filters.priorPeriodEnd) {
        paramStrings.push(`prior_period_end=${filters.priorPeriodEnd}`);
    }
    if (filters.granularity) {
        paramStrings.push(`granularity=${filters.granularity}`);
    }
    if (filters.businessUnitId) {
        paramStrings.push(`business_unit=${filters.businessUnitId}`);
    }
    if (filters.salesPersonId) {
        paramStrings.push(`sales_territory=SP${filters.salesPersonId}`);
    } else if (filters.locationId) {
        paramStrings.push(`sales_territory=${filters.locationId}`);
    }
    if (filters.accountId) {
        paramStrings.push(`account=${filters.accountId}`);
    }
    if (filters.productCategoryId) {
        paramStrings.push(`product=${filters.productCategoryId}`);
    }
    return paramStrings.length > 0 ? `?${paramStrings.join('&')}` : '';
};

export const BREAKDOWN_VIEWS = {
    CUSTOMERS: 'customers',
    SALES_REPS: 'sales_reps',
    PRODUCTS: 'products'
};

export const buildV2BreakdownQueryParams = (filters, period = 'current', options = {}) => {
    const normalized = typeof options === 'number' ? {accountId: options} : options;
    const resolvedAccountId = normalized.accountId != null ? normalized.accountId : filters.accountId;
    const breakdownView = normalized.breakdownView || BREAKDOWN_VIEWS.CUSTOMERS;
    const effective = {
        ...filters,
        accountId: resolvedAccountId
    };
    const baseParams = period === 'prior'
        ? buildV2QueryParams({
            ...effective,
            currentPeriodStart: effective.priorPeriodStart,
            currentPeriodEnd: effective.priorPeriodEnd
        })
        : buildV2QueryParams(effective);

    const extraParams = [];
    if (breakdownView === BREAKDOWN_VIEWS.SALES_REPS) {
        extraParams.push('group_by=sales_person');
    } else if (breakdownView === BREAKDOWN_VIEWS.PRODUCTS) {
        extraParams.push('group_by=product');
    } else {
        extraParams.push('group_by=account');
    }

    if (!extraParams.length) {
        return baseParams;
    }
    const separator = baseParams.includes('?') ? '&' : '?';
    return `${baseParams}${separator}${extraParams.join('&')}`;
};

export const hasDateFilters = (filters) => {
    return Boolean(
        filters?.currentPeriodStart &&
        filters?.currentPeriodEnd &&
        filters?.priorPeriodStart &&
        filters?.priorPeriodEnd
    );
};

export const getLegacyPeriod = (filters) => filters.period || 'YTD';

export const getLegacyGranularity = (filters) => filters.granularity || 'M';

export const buildLegacyQueryParams = (filters) => {
    const paramStrings = [];
    if (filters.currentPeriodStart) {
        const year = DateTime.fromISO(filters.currentPeriodStart).year;
        paramStrings.push(`year=${year}`);
    }
    for (const param of ['businessUnitId', 'accountId', 'locationId', 'productCategoryId', 'salesPersonId']) {
        if (filters[param]) {
            paramStrings.push(`${param}=${filters[param]}`);
        }
    }
    return paramStrings.length > 0 ? `?${paramStrings.join('&')}` : '';
};

export const buildLegacyApiContext = (filters) => ({
    period: getLegacyPeriod(filters),
    granularity: getLegacyGranularity(filters),
    queryParams: buildLegacyQueryParams(filters)
});

export const appendQueryString = (url, extra) => {
    if (!extra) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${extra}`;
};

export const buildLegacyAuditUrl = (path, filters) => {
    const {period, queryParams} = buildLegacyApiContext(filters);
    return `calc/audit/${path}/period/${period}${queryParams}`;
};

export const buildLegacyAuditTrendUrl = (path, filters) => {
    const {period, granularity, queryParams} = buildLegacyApiContext(filters);
    return `calc/audit/${path}/period/${period}/granularity/${granularity}${queryParams}`;
};

export const normalizeLocationTreeItems = (items, parentPath = '') => {
    return items.map((item) => {
        const rawId = String(item.id);
        const treeId = parentPath ? `${parentPath}::${rawId}` : rawId;
        const children = item.children?.length
            ? normalizeLocationTreeItems(item.children, treeId)
            : undefined;
        return {
            id: treeId,
            label: item.label || item.name,
            business_unit_id: item.business_unit_id,
            children
        };
    });
};

export const parseLocationTreeSelection = (treeId) => {
    if (treeId === 'ALL') {
        return {type: 'all'};
    }
    const id = Array.isArray(treeId) ? treeId[0] : treeId;
    if (typeof id !== 'string') {
        return {type: 'location', locationId: id};
    }
    const spMatch = id.match(/SP(\d+)$/);
    if (spMatch) {
        return {type: 'salesPerson', salesPersonId: parseInt(spMatch[1], 10)};
    }
    const locationId = id.includes('::') ? id.split('::').pop() : id;
    return {type: 'location', locationId};
};

export const buildQueryParams = (dateSpanFilters, otherFilters, reduceTo = null) => {
    const paramStrings = [];
    for (const param of ['year', 'quarter', 'month']) {
        if (dateSpanFilters[param]) paramStrings.push(`${param}=${dateSpanFilters[param]}`);
    }
    for (const param of ['businessUnitId', 'accountId', 'locationId', 'productCategoryId', 'salesPersonId']) {
        if (otherFilters[param]) paramStrings.push(`${param}=${otherFilters[param]}`);
    }
    if (reduceTo) paramStrings.push(`${reduceTo}=true`);
    return paramStrings.length > 0 ? `?${paramStrings.join('&')}` : '';
};

export const filterAccountTreeBySearch = (nodes, searchText) => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
        return nodes;
    }

    const filterNode = (node) => {
        const label = (node.label || node.name || '').toLowerCase();
        const address = (node.address || '').toLowerCase();
        const number = (node.number || '').toLowerCase();
        const matchesSelf = label.includes(query) || address.includes(query) || number.includes(query);
        const filteredChildren = (node.children || [])
            .map(filterNode)
            .filter(Boolean);

        if (matchesSelf || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren.length > 0 ? filteredChildren : undefined
            };
        }
        return null;
    };

    return nodes.map(filterNode).filter(Boolean);
};

export const filterAccountsByBusinessUnit = (accounts, businessUnitId) => {
    if (!businessUnitId) {
        return accounts;
    }
    const buId = Number(businessUnitId);
    return accounts.filter((account) => Number(account.business_unit_id) === buId);
};

export const filterAccountTreeByBusinessUnit = (accounts, businessUnitId) => {
    if (!businessUnitId) {
        return accounts;
    }
    const buId = Number(businessUnitId);
    const filterNode = (node) => {
        const children = (node.children || [])
            .map(filterNode)
            .filter(Boolean);
        if (Number(node.business_unit_id) === buId) {
            return {...node, children: children.length > 0 ? children : undefined};
        }
        if (children.length > 0) {
            return {...node, children};
        }
        return null;
    };
    return accounts.map(filterNode).filter(Boolean);
};

export const accountExistsInTree = (accounts, accountId) => {
    for (const account of accounts) {
        if (account.id === accountId) {
            return true;
        }
        if (account.children && accountExistsInTree(account.children, accountId)) {
            return true;
        }
    }
    return false;
};

export const getBusinessUnitLabelPrefix = (businessUnits, nodeBusinessUnitId) => {
    if (!nodeBusinessUnitId) {
        return null;
    }
    const bu = businessUnits.find((item) => Number(item.id) === Number(nodeBusinessUnitId));
    return bu?.abbr || bu?.name || null;
};

export const formatFilterTreeLabel = (name, businessUnits, filterBusinessUnitId, nodeBusinessUnitId) => {
    if (filterBusinessUnitId) {
        return name;
    }
    const prefix = getBusinessUnitLabelPrefix(businessUnits, nodeBusinessUnitId);
    return prefix ? `${prefix}-${name}` : name;
};

export const buildLabeledFilterTree = (nodes, businessUnits, filterBusinessUnitId, options = {}) => {
    const shouldPrefixNode = options.shouldPrefixNode || (() => true);
    const mapNode = (node) => {
        const baseLabel = node.label || node.name;
        const label = shouldPrefixNode(node)
            ? formatFilterTreeLabel(baseLabel, businessUnits, filterBusinessUnitId, node.business_unit_id)
            : baseLabel;
        const children = node.children?.map(mapNode);
        return {
            ...node,
            label,
            children: children?.length ? children : undefined
        };
    };
    return nodes.map(mapNode);
};

export const buildTreeIdToLabelMap = (nodes, resultMap = {'ALL': 'ALL'}) => {
    for (const node of nodes) {
        resultMap[node.id] = node.label || node.name;
        if (node.children) {
            buildTreeIdToLabelMap(node.children, resultMap);
        }
    }
    return resultMap;
};

export const filterProductCategoryTreeByBusinessUnit = (categories, businessUnitId) => {
    if (!businessUnitId) {
        return categories;
    }
    const buId = Number(businessUnitId);
    const filterNode = (node) => {
        if (Number(node.business_unit_id) !== buId) {
            return null;
        }
        const children = (node.children || [])
            .map(filterNode)
            .filter(Boolean);
        return {...node, children: children.length > 0 ? children : undefined};
    };
    return categories.map(filterNode).filter(Boolean);
};

export const productCategoryExistsInTree = (categories, productCategoryId) => {
    for (const category of categories) {
        if (category.id === productCategoryId) {
            return true;
        }
        if (category.children && productCategoryExistsInTree(category.children, productCategoryId)) {
            return true;
        }
    }
    return false;
};

export const determinePreviousDateSpanFields = (comparison, year, quarter, month) => {
    //TODO: consider using only the years, quarters, and months that are actually present in the data...
    let prevDateFields;
    if (comparison === 'YOY') {
        prevDateFields = {year: year - 1, quarter: quarter, month: month};
    } else if (comparison === 'QOQ') {
        const prevQuarter = quarter === 1 ? 4 : quarter - 1;
        prevDateFields = {year: (prevQuarter === 4 ? year - 1 : year), quarter: prevQuarter, month: month};
    } else if (comparison === 'MOM') {
        const prevMonth = month === 1 ? 12 : month - 1;
        prevDateFields = {year: (prevMonth === 12 ? year - 1 : year), quarter: quarter, month: prevMonth};
    }
    return prevDateFields;
};
